import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../i18n";
import { Reveal, Eyebrow } from "./Reveal";
import { MENU_ITEMS, LUNCH_MENU_ITEMS } from "../data";

const CATS_DINNER = ["fritti", "antipasti", "primi", "secondi", "dolci"];
const CATS_LUNCH = ["insalate", "fritti", "fattoria", "primiTradizione", "primiCasa", "secondi", "dolci"];

export const MenuSection = () => {
  const { lang, t } = useLanguage();
  const [service, setService] = useState("cena");
  const [cat, setCat] = useState("fritti");
  const cats = service === "cena" ? CATS_DINNER : CATS_LUNCH;
  const source = service === "cena" ? MENU_ITEMS : LUNCH_MENU_ITEMS;
  const items = source.filter((m) => m.cat === cat);

  const switchService = (s) => {
    setService(s);
    setCat(s === "cena" ? "fritti" : "insalate");
  };

  return (
    <section
      id="menu"
      data-testid="restaurant-menu-section"
      className="relative py-24 sm:py-36"
      style={{ backgroundColor: "#FDFBF7" }}
    >
      <div className="max-w-4xl mx-auto px-6 sm:px-10">
        <div className="text-center">
          <Reveal>
            <Eyebrow>{t.menu.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-display font-light text-4xl sm:text-5xl tracking-tight text-[#2C0F1A]">
              {t.menu.title}
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 text-sm sm:text-base leading-relaxed text-[#5A4440] max-w-xl mx-auto">
              {t.menu.intro}
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div data-testid="menu-service-switch" className="mt-10 flex justify-center gap-3">
            {["pranzo", "cena"].map((s) => (
              <button
                key={s}
                data-testid={`menu-service-${s}-btn`}
                onClick={() => switchService(s)}
                className={`px-7 py-3 rounded-full border font-meta text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                  service === s
                    ? "bg-[#480A1A] text-[#F5EFE6] border-[#480A1A]"
                    : "border-[#480A1A]/25 text-[#480A1A]/70 hover:border-[#480A1A] hover:text-[#480A1A]"
                }`}
              >
                {s === "pranzo" ? t.menu.lunch : t.menu.dinner}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.24}>
          <div
            data-testid="menu-tabs"
            className="mt-8 flex flex-wrap justify-center gap-x-7 gap-y-3"
          >
            {cats.map((c) => (
              <button
                key={c}
                data-testid={`menu-tab-${c}`}
                onClick={() => setCat(c)}
                className={`relative pb-2 font-meta text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                  cat === c ? "text-[#480A1A]" : "text-[#5A4440]/60 hover:text-[#480A1A]"
                }`}
              >
                {t.menu.categories[c]}
                {cat === c && (
                  <motion.span
                    key={service}
                    layoutId="menu-tab-underline"
                    className="absolute left-0 right-0 bottom-0 h-px bg-[#C69255]"
                  />
                )}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 min-h-[340px]">
          <AnimatePresence mode="wait">
            <motion.ul
              key={service + cat + lang}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="space-y-9"
            >
              {items.map((item, i) => (
                <li key={i} data-testid={`menu-item-${cat}-${i}`}>
                  <div className="flex items-baseline">
                    <h3 className="font-display text-xl sm:text-2xl text-[#2C0F1A] leading-snug max-w-[75%]">
                      {lang === "en" ? item.name_en : item.name}
                    </h3>
                    <span className="leader-dots hidden sm:block" />
                    <span className="ml-auto sm:ml-0 font-meta text-sm text-[#A5713C] whitespace-nowrap pl-3">
                      {item.price} €
                    </span>
                  </div>
                  {(lang === "en" ? item.desc_en : item.desc) && (
                    <p className="mt-1.5 text-sm text-[#5A4440]/85 max-w-xl">
                      {lang === "en" ? item.desc_en : item.desc}
                    </p>
                  )}
                </li>
              ))}
            </motion.ul>
          </AnimatePresence>
        </div>

        <Reveal delay={0.1}>
          <p
            data-testid="menu-seasonal-note"
            className="mt-16 text-center font-display italic text-base text-[#A5713C]"
          >
            {t.menu.note}
          </p>
          <p className="mt-3 text-center font-meta text-[10px] uppercase tracking-[0.22em] text-[#5A4440]/60">
            {t.menu.allergenNote}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
